/**
 * F42 (Wave 2 — B2) — venue suspend / restore drawer.
 *
 * Suspend: mandatory ≥10-char reason + a preview of the F26 owner notification
 * + a destructive confirm dialog → `onSubmit({ reason })`.
 * Restore: optional reason → `onSubmit({})` or `onSubmit({ reason })`.
 *
 * The drawer is presentation-only: B1's venue detail page passes `onSubmit`
 * wired to `adminVenuesApi.suspend` / `.restore` and owns the toast + refetch.
 * Design-flagged for a later polish pass (the notification preview card).
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Button, Dialog, Drawer, FormField, Textarea } from '@bravemove/ui-web';
import { SUSPEND_REASON_MIN } from '@bravemove/types';

export type SuspendDrawerMode = 'suspend' | 'restore';

export interface SuspendDrawerProps {
  readonly open: boolean;
  readonly mode: SuspendDrawerMode;
  readonly venueName: string;
  readonly onClose: () => void;
  /** Suspend → `{ reason }`; restore → `{}` or `{ reason }`. */
  readonly onSubmit: (input: { reason?: string }) => Promise<void>;
}

export function SuspendDrawer({ open, mode, venueName, onClose, onSubmit }: SuspendDrawerProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- reset transient state when re-opened */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setConfirmOpen(false);
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const isSuspend = mode === 'suspend';
  const trimmed = reason.trim();
  const reasonValid = !isSuspend || trimmed.length >= SUSPEND_REASON_MIN;

  const title = isSuspend
    ? t('admin:venueMerge.suspendDrawer.suspendTitle', { defaultValue: 'Suspend venue' })
    : t('admin:venueMerge.suspendDrawer.restoreTitle', { defaultValue: 'Restore venue' });

  const notificationPreview = isSuspend
    ? t('admin:venueMerge.suspendDrawer.suspendNotificationPreview', {
        defaultValue:
          'Your venue has been suspended by BraveMove. Reason: {reason}. To appeal, contact support@bravemove.app.',
        reason: trimmed.length > 0 ? trimmed : '…',
      })
    : t('admin:venueMerge.suspendDrawer.restoreNotificationPreview', {
        defaultValue: 'Your venue has been restored and is live again on BraveMove.',
      });

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(trimmed.length > 0 ? { reason: trimmed } : {});
      setConfirmOpen(false);
      onClose();
    } catch {
      // The caller surfaces the error (toast); keep the drawer open so the
      // admin can retry without retyping the reason.
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimary = () => {
    if (isSuspend) {
      setConfirmOpen(true);
    } else {
      void submit();
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={title}
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              {t('admin:venueMerge.suspendDrawer.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant={isSuspend ? 'destructive' : 'primary'}
              onClick={handlePrimary}
              disabled={!reasonValid || submitting}
              loading={!isSuspend && submitting}
              data-testid="suspend-drawer-cta"
            >
              {isSuspend
                ? t('admin:venueMerge.suspendDrawer.suspendCta', { defaultValue: 'Suspend venue' })
                : t('admin:venueMerge.suspendDrawer.restoreCta', { defaultValue: 'Restore venue' })}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4" data-venue-name={venueName}>
          <p className="text-[13px] text-ink-2 leading-snug">{venueName}</p>

          <FormField
            label={
              isSuspend
                ? t('admin:venueMerge.suspendDrawer.suspendReasonLabel', {
                    defaultValue: 'Reason for suspension',
                  })
                : t('admin:venueMerge.suspendDrawer.restoreReasonLabel', {
                    defaultValue: 'Reason (optional)',
                  })
            }
            helper={
              isSuspend
                ? t('admin:venueMerge.suspendDrawer.suspendReasonHelper', {
                    defaultValue:
                      'Required — shown to the owner and recorded in the audit log (min 10 characters).',
                  })
                : t('admin:venueMerge.suspendDrawer.restoreReasonHelper', {
                    defaultValue: 'Optional note recorded in the audit log.',
                  })
            }
            required={isSuspend}
          >
            <Textarea
              rows={4}
              maxLength={2000}
              showCounter
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="suspend-drawer-reason"
            />
          </FormField>

          <div className="rounded-md border border-hairline-2 bg-surface-2 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 mb-1">
              {t('admin:venueMerge.suspendDrawer.notificationHeading', {
                defaultValue: 'The venue owner will be notified',
              })}
            </p>
            <p
              className="text-[12.5px] text-ink-2 leading-snug"
              data-testid="suspend-drawer-notification"
            >
              {notificationPreview}
            </p>
          </div>
        </div>
      </Drawer>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void submit()}
        destructive
        loading={submitting}
        title={t('admin:venueMerge.suspendDrawer.confirmSuspendTitle', {
          defaultValue: 'Suspend this venue?',
        })}
        description={t('admin:venueMerge.suspendDrawer.confirmSuspendBody', {
          defaultValue:
            'The venue is hidden from discovery and new bookings are paused. Existing bookings are kept.',
        })}
        confirmLabel={t('admin:venueMerge.suspendDrawer.confirmSuspendCta', {
          defaultValue: 'Suspend',
        })}
        cancelLabel={t('admin:venueMerge.suspendDrawer.cancel', { defaultValue: 'Cancel' })}
      >
        <p data-testid="suspend-drawer-confirm-body" className="sr-only">
          {venueName}
        </p>
      </Dialog>
    </>
  );
}
